import messages from '@/shared/i18n/en/home-tabs';
import { tr } from '@/shared/i18n/translate';

import type { HomeBlockId } from './home-composition';

/**
 * Home tabs (founder 2026-10-04): "Overview" keeps everything together; the
 * other tabs duplicate one area each so a person can jump straight to it
 * instead of scrolling — the same idea as the project page tabs.
 * URL: `/?tab=tasks` etc. (`overview` = no param).
 */
export type HomeTab = 'overview' | 'tasks' | 'analytics' | 'activity';

const TAB_LABEL_KEY = {
  overview: 'tabOverview',
  tasks: 'tabTasks',
  analytics: 'tabAnalytics',
  activity: 'tabActivity',
} as const satisfies Record<HomeTab, keyof typeof messages.en>;

/** Labels read the current language on every access (never frozen at import). */
export const HOME_TABS: ReadonlyArray<{ readonly id: HomeTab; readonly label: string }> = (
  ['overview', 'tasks', 'analytics', 'activity'] as const
).map((id) => ({
  id,
  get label() {
    return tr(messages, TAB_LABEL_KEY[id]);
  },
}));

export function parseHomeTab(raw: unknown): HomeTab | undefined {
  return HOME_TABS.some((t) => t.id === raw) && raw !== 'overview' ? (raw as HomeTab) : undefined;
}

/**
 * Blocks shown on each tab, in this order. Role rules still apply: a tab
 * renders only the blocks `composeHome` produced for me (e.g. Team pulse is
 * Lead+). `overview` = every composed block, unchanged.
 */
export const TAB_BLOCKS: Record<Exclude<HomeTab, 'overview'>, readonly HomeBlockId[]> = {
  tasks: ['board'],
  analytics: ['stats', 'momentum', 'workload', 'teams', 'pulse', 'week'],
  activity: ['attention', 'activity', 'follow', 'favorites'],
};

export function blocksForTab(tab: HomeTab, composed: readonly HomeBlockId[]): HomeBlockId[] {
  if (tab === 'overview') return [...composed];
  return TAB_BLOCKS[tab].filter((id) => composed.includes(id));
}
